/* eslint-disable @typescript-eslint/naming-convention */
import type { FC } from "react";
import { useMemo, useState } from "react";

import { useDispatch } from "react-redux";

import type { BacklinksRefdomainsCreatePayload } from "@similarweb/pro-api-client";
import { Table } from "@similarweb/pro-logic-components";

import { showErrorToast } from "app/actions/toast_actions";
import { QualificationSideBarHOC } from "app/components/QualificationSideBar/QualificationSideBarHOC";
import { allTrackers } from "app/services/track/track";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { BacklinksBodyContainer } from "app/pages/digital-marketing/backlinks/shared/BacklinksStyledComponents";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useBacklinksDomainApi } from "../../shared/backlinks-api-service";
import { DEFAULT_PAGE_SIZE } from "../../shared/BacklinksConsts";
import type { IBacklinksTable } from "../../shared/BacklinksTypes";
import {
    buildBacklinksRefdomainsCreateParams,
    getExcelDownloadURL,
    tablePaginationConfig,
    useExpectedBacklinksParams,
} from "../../shared/BacklinksUtils";

import { getBacklinksDomainsColumns } from "./components/BacklinksDomainsColumns";
import { BacklinksDomainsTableContainer } from "./components/BacklinksDomainsStyledComponents";
import { BacklinksDomainsTableFilters } from "./components/filters/BacklinksDomainsTableFilters";

const BacklinksDomainsReportComponent: FC<IBacklinksTable> = ({ handleOpenQualificationSideBar }) => {
    const params = useExpectedBacklinksParams();

    const translate = useTranslation();
    const dispatch = useDispatch();
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
    });

    const dataParamsAdapter = buildBacklinksRefdomainsCreateParams(params, pagination);

    const dataPayloadAdapter: BacklinksRefdomainsCreatePayload = params.targetUrl
        ? [
              {
                  fieldName: "urlTo",
                  items: [params.targetUrl],
                  matchType: "exact",
                  type: "include",
              },
          ]
        : [];

    const {
        data: refDomains,
        isLoading: refDomainsLoading,
        isFetching: refDomainsFetching,
        isError: refDomainsError,
    } = useBacklinksDomainApi(dataParamsAdapter, dataPayloadAdapter);

    const tableColumns = useMemo(() => {
        return getBacklinksDomainsColumns({
            handleOpenQualificationSideBar: (row) => {
                handleOpenQualificationSideBar?.({ ...row, ComponentId: "WebAnalysisOverviewOverTime" });
            },
            params,
            translate,
            rowsCount: refDomains?.TotalRecords,
            currentPage: pagination.page,
        });
    }, [handleOpenQualificationSideBar, params, translate, refDomains?.TotalRecords, pagination.page]);

    const excelUrl = getExcelDownloadURL("domainsExcel", params);

    const handleExcelClick = () => {
        allTrackers.dispatchTrackEvent(
            "search.backlinks.domains.excel.click",
            "click",
            "search.backlinks.domains.excel.click",
        );
    };

    const showDataActions = !refDomainsError && refDomains?.Records && refDomains.Records.length > 0;

    return (
        <BacklinksBodyContainer>
            <WidgetContainer
                subtitleFilters={null}
                isLoading={false}
                loaderWidth="100%"
                excelUrl={showDataActions ? excelUrl : undefined}
                requestBodyForExcel={dataPayloadAdapter}
                onExcelClick={handleExcelClick}
                withBorder={true}
                title={<BacklinksDomainsTableFilters />}
                headerHeight={82}
                downloadProps={{
                    showToast: () => dispatch(showErrorToast(translate("global.download.excel.error"))),
                }}
            >
                <BacklinksDomainsTableContainer>
                    <Table
                        columnConfig={tableColumns}
                        data={refDomains?.Records ?? []}
                        isLoading={refDomainsLoading || refDomainsFetching}
                        pagination={tablePaginationConfig({
                            ...refDomains,
                            onChange: (_page, _pageSize, nextPage) => {
                                setPagination((prev) => ({ ...prev, page: nextPage }));
                            },
                        })}
                        onSort={(sortData) => {
                            allTrackers.dispatchTrackEvent("Sort", "Click", sortData.field as string);
                            void proNavigator.updateParams({ asc: sortData.order === "ascend", sort: sortData.field });
                        }}
                        noData={{
                            title: translate("global.nodata.notavilable"),
                            subtitle: translate("global.nodata.notavilable.subtitle"),
                        }}
                        rowKey="Name"
                        sticky={{
                            offsetHeader: -32,
                        }}
                    />
                </BacklinksDomainsTableContainer>
            </WidgetContainer>
        </BacklinksBodyContainer>
    );
};

export const BacklinksDomainsReport = QualificationSideBarHOC(BacklinksDomainsReportComponent);
